import { useEffect, useMemo, useState } from 'react';

/**
 * Animated digit-by-digit counter. Each digit slides vertically when its
 * value changes. Tabular-nums + monospace keep widths stable.
 */
const Digit = ({ value, fontSize, fontWeight, color }) => {
  // value is a number 0-9 or null (placeholder)
  const items = Array.from({ length: 10 }, (_, i) => i);
  const offset = value == null ? 0 : value;
  return (
    <div
      className="counter-digit"
      style={{
        height: `${fontSize}px`,
        fontSize: `${fontSize}px`,
        fontWeight,
        color,
      }}
    >
      <div
        className="counter-digit-track"
        style={{
          transform: `translateY(-${offset * fontSize}px)`,
        }}
      >
        {items.map((n) => (
          <span
            key={n}
            className="counter-digit-cell"
            style={{ height: `${fontSize}px`, lineHeight: `${fontSize}px` }}
          >
            {value == null ? '–' : n}
          </span>
        ))}
      </div>
    </div>
  );
};

const Counter = ({
  value = 0,
  places = [100, 10, 1],
  fontSize = 80,
  padding = 5,
  gap = 6,
  textColor = 'white',
  fontWeight = 700,
  digitPlaceHolders = false,
}) => {
  const [display, setDisplay] = useState(0);

  // Smoothly ramp from current displayed value to target value
  useEffect(() => {
    const start = display;
    const end = value;
    if (start === end) return;
    const duration = Math.min(1400, 400 + Math.abs(end - start) * 30);
    const startedAt = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - startedAt) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const cur = Math.round(start + (end - start) * eased);
      setDisplay(cur);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const digits = useMemo(() => {
    return places.map((p) => {
      const d = Math.floor(display / p) % 10;
      // If digitPlaceHolders is false, hide leading zero-only digits
      const isLeading = p > display && p !== 1;
      if (!digitPlaceHolders && isLeading && display < p) return null;
      return d;
    });
  }, [display, places, digitPlaceHolders]);

  return (
    <div
      className="counter-container"
      style={{ padding: `${padding}px`, gap: `${gap}px` }}
    >
      <div className="counter-counter" style={{ gap: `${gap}px` }}>
        {digits.map((d, idx) =>
          d === null ? null : (
            <Digit
              key={idx}
              value={d}
              fontSize={fontSize}
              fontWeight={fontWeight}
              color={textColor}
            />
          )
        )}
      </div>
    </div>
  );
};

export default Counter;
